import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IconBuildingStore, IconFileDownload, IconLayoutGrid, IconListDetails, IconPalette } from '@tabler/icons-react';
import PosterEditor from '@/components/common/PosterEditor';
import { cn } from '@/lib/utils';
import { sanitizeUrl } from '@/utils/sanitize';
import { fileStem } from '@/utils/posterExport';
import questionPaperService from '@/services/questionPaperService';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import {
    DENSITIES, PAGE_LABEL_SLACK, PAPERS, PRESETS, SHEET_W, STRIP_H,
    formatPrice, getLayout, getTheme, layoutMetrics, localizeDigits, packRows,
} from './rateChartConfig';
import { RAIL, TEXT_KEYS, buildInitialState } from './rateChartModel';
import { SheetPage, SheetRuler } from './RateChartSheet';
import { loadPosterFonts, makeQrDataUrl } from './rateChartExport';
import TemplatePanel from './panels/TemplatePanel';
import DesignPanel from './panels/DesignPanel';
import ServicesPanel from './panels/ServicesPanel';
import ShopPanel from './panels/ShopPanel';

const RAIL_ICONS = { tpl: IconLayoutGrid, des: IconPalette, svc: IconListDetails, shop: IconBuildingStore };

/**
 * The rate-chart poster: what is on the sheet and the four panels that change it. The editor window itself (canvas, zoom, tabs,
 * PDF / image / print) is the shared <PosterEditor>. Everything is local: nothing here is saved or sent anywhere.
 *
 * Not used on its own — open it through <RateChartModal> / <RateChartButton> (components/common), which give it the full-screen
 * window and the starting data.
 */
export default function RateChartEditor({ services, shop, onClose }) {
    const { t, i18n } = useTranslation();
    const uiLang = i18n.language?.startsWith('en') ? 'en' : 'bn';

    const [s, setS] = useState(() => buildInitialState({
        services, shop, lang: uiLang, tBn: i18n.getFixedT('bn'), tEn: i18n.getFixedT('en'), year: new Date().getFullYear(),
    }));
    const patch = useCallback((p) => setS((prev) => ({ ...prev, ...(typeof p === 'function' ? p(prev) : p) })), []);

    const [query, setQuery] = useState('');
    const [qrSrc, setQrSrc] = useState('');
    const [fontTick, setFontTick] = useState(0); // bumps when fonts finish loading → pages are measured again

    const tp = useMemo(() => i18n.getFixedT(s.lang), [i18n, s.lang]); // the POSTER's language, not the app's

    // ── display model ──
    const th = useMemo(() => getTheme(s.theme), [s.theme]);
    const tk = useMemo(() => getLayout(s.layout, th), [s.layout, th]);
    const d = DENSITIES[s.density];
    const H = (PAPERS[s.paper] || PAPERS.A4).h;
    const metrics = useMemo(() => layoutMetrics(tk, d, s.cols), [tk, d, s.cols]);

    const rows = useMemo(() => s.items.filter((it) => !s.off[it.id]).map((it) => {
        const price = formatPrice(it.price, s.lang);
        return { id: it.id, name: it.names[s.lang] || it.names.bn || it.names.en || '', price, hasPrice: price !== '' };
    }), [s.items, s.off, s.lang]);

    const text = useMemo(() => ({
        kicker: s.texts.kicker[s.lang], title: s.texts.title[s.lang], subtitle: s.texts.subtitle[s.lang], note: s.texts.note[s.lang],
        shop: s.shop, phone: localizeDigits(s.phone, s.lang), addr: s.addr, year: localizeDigits(s.year, s.lang),
        phoneLabel: tp('rateChart.poster.mobile'), scanMe: tp('rateChart.poster.scanMe'), taka: tp('rateChart.poster.currency'), negotiable: tp('rateChart.poster.negotiable'),
        pageLabel: (n, total) => tp('rateChart.poster.page', { n: localizeDigits(n, s.lang), total: localizeDigits(total, s.lang) }),
    }), [s.texts, s.lang, s.shop, s.phone, s.addr, s.year, tp]);

    // QR: only a clean https link is ever encoded
    const qrClean = useMemo(() => (s.qr ? sanitizeUrl(s.qrUrl, { maxLen: 300 }) : ''), [s.qr, s.qrUrl]);
    const qrInvalid = s.qr && s.qrUrl.trim() !== '' && !qrClean;
    useEffect(() => {
        let alive = true;
        makeQrDataUrl(qrClean).then((url) => { if (alive) setQrSrc(url); });
        return () => { alive = false; };
    }, [qrClean]);

    const m = useMemo(() => ({
        th, tk, d, cols: s.cols, H, text, metrics,
        logoSrc: s.logoSrc, qrSrc: s.qr ? qrSrc : '', showYear: s.showYear, showWm: s.wm,
    }), [th, tk, d, s.cols, H, text, metrics, s.logoSrc, s.qr, qrSrc, s.showYear, s.wm]);

    // ── fonts: the poster's glyphs load on demand; pages are measured again once they are there ──
    useEffect(() => {
        let alive = true;
        loadPosterFonts().then(() => { if (alive) setFontTick((n) => n + 1); });
        const onLoaded = () => setFontTick((n) => n + 1);
        document.fonts?.addEventListener?.('loadingdone', onLoaded);
        return () => { alive = false; document.fonts?.removeEventListener?.('loadingdone', onLoaded); };
    }, []);

    // The shop's saved logo (a files.sohozkaj.com URL the browser can't read for export) comes in through the image proxy.
    useEffect(() => {
        if (!shop.logoUrl) return undefined;
        let alive = true;
        questionPaperService.proxyImageDataUrl(shop.logoUrl)
            .then((url) => { if (alive) setS((prev) => (prev.logoSrc ? prev : { ...prev, logoSrc: url })); })
            .catch(() => { /* no logo is fine */ });
        return () => { alive = false; };
    }, [shop.logoUrl]);

    // ── pagination: measure the real blocks, then pack ──
    const rulerRef = useRef(null);
    const [pages, setPages] = useState(null);
    useLayoutEffect(() => {
        const el = rulerRef.current;
        if (!el) return;
        // The window may still be zooming in (a transform on an ancestor scales every rect) → measure in layout pixels.
        const scale = el.getBoundingClientRect().width / SHEET_W || 1;
        const hOf = (node) => (node ? node.getBoundingClientRect().height / scale : 0);
        const headH = hOf(el.querySelector('[data-rc-head]'));
        const footH = hOf(el.querySelector('[data-rc-foot]'));
        const heights = Array.from(el.querySelectorAll('[data-rc-row]'), hOf);
        const capacity = (i) => H - tk.pageInset - (i === 0 ? headH : STRIP_H) - metrics.padT - metrics.padB - tk.wrapY - PAGE_LABEL_SLACK;
        setPages(packRows({ heights, cols: s.cols, capacity, footH }));
    }, [m, rows, fontTick]); // eslint-disable-line react-hooks/exhaustive-deps

    const pageList = pages || [];
    const columnsOf = (pg) => pg.columns.map((col) => col.map((i) => rows[i]).filter(Boolean));

    // ── handlers ──
    const presetId = PRESETS.find((p) => p.theme === s.theme && p.layout === s.layout && p.density === s.density && p.cols === s.cols)?.id ?? null;
    const pickPreset = (p) => patch({ theme: p.theme, layout: p.layout, density: p.density, cols: p.cols });

    const onText = (key, value) => patch((prev) => (
        TEXT_KEYS.includes(key) ? { texts: { ...prev.texts, [key]: { ...prev.texts[key], [prev.lang]: value } } } : { [key]: value }
    ));
    const onName = (id, value) => patch((prev) => ({ items: prev.items.map((it) => (it.id === id ? { ...it, names: { ...it.names, [prev.lang]: value } } : it)) }));
    const onPrice = (id, value) => patch((prev) => ({ items: prev.items.map((it) => (it.id === id ? { ...it, price: value } : it)) }));
    const onToggle = (id) => patch((prev) => {
        const off = { ...prev.off };
        if (off[id]) delete off[id]; else off[id] = true;
        return { off };
    });
    const onAll = (on) => patch((prev) => ({ off: on ? {} : Object.fromEntries(prev.items.map((it) => [it.id, true])) }));
    const onDelete = (id) => patch((prev) => {
        const off = { ...prev.off };
        delete off[id];
        return { items: prev.items.filter((it) => it.id !== id), off };
    });
    const onAdd = () => {
        const id = `c${Date.now().toString(36)}`;
        patch((prev) => ({
            items: [...prev.items, { id, category: null, custom: true, names: { bn: i18n.getFixedT('bn')('rateChart.services.newName'), en: i18n.getFixedT('en')('rateChart.services.newName') }, price: '' }],
        }));
        setQuery('');
        requestAnimationFrame(() => requestAnimationFrame(() => {
            const input = document.querySelector(`[data-rc-svc="${id}"] input`);
            input?.focus();
            input?.select();
        }));
    };

    // plural is chosen by the real number (`count`); the digits shown follow the app's language (`n`)
    const countLabel = (key, count) => t(`rateChart.${key}`, { count, n: localizeDigits(count, uiLang) });

    // ── panels ──
    const shopValues = {
        title: s.texts.title[s.lang], subtitle: s.texts.subtitle[s.lang], kicker: s.texts.kicker[s.lang], note: s.texts.note[s.lang],
        shop: s.shop, phone: s.phone, addr: s.addr, year: s.year,
    };
    const panelBody = {
        tpl: <TemplatePanel currentId={presetId} onPick={pickPreset} />,
        des: <DesignPanel theme={s.theme} layout={s.layout} density={s.density} cols={s.cols} paper={s.paper} onChange={patch} />,
        svc: <ServicesPanel items={s.items} off={s.off} lang={s.lang} query={query} negotiable={text.negotiable} onQuery={setQuery} onToggle={onToggle} onAll={onAll} onName={onName} onPrice={onPrice} onDelete={onDelete} onAdd={onAdd} />,
        shop: (
            <ShopPanel
                values={shopValues} onText={onText}
                logoSrc={s.logoSrc} onLogo={(v) => patch({ logoSrc: v })}
                qr={s.qr} onQr={(v) => patch({ qr: v })} qrUrl={s.qrUrl} onQrUrl={(v) => patch({ qrUrl: v })} qrInvalid={qrInvalid} qrPreview={qrSrc}
                showYear={s.showYear} onShowYear={(v) => patch({ showYear: v })} wm={s.wm} onWm={(v) => patch({ wm: v })}
            />
        ),
    };
    const tabs = RAIL.map((id) => ({
        id,
        label: t(`rateChart.rail.${id}.label`),
        title: t(`rateChart.rail.${id}.title`),
        desc: t(`rateChart.rail.${id}.desc`),
        Icon: RAIL_ICONS[id],
        content: panelBody[id],
    }));

    // ── what the shared editor needs ──
    const pageCount = pageList.length || 1;
    const metaLine = [countLabel('top.services', rows.length), countLabel('top.page', pageCount), s.paper].join(' · ');

    // poster language — pill chips, like the ratio chips of the crop window
    const langPills = (
        <div className="flex items-center gap-1.5 shrink-0" role="group" aria-label={t('rateChart.top.language')}>
            {[['bn', 'বাংলা'], ['en', 'EN']].map(([code, label]) => (
                <button
                    key={code}
                    type="button"
                    onClick={() => patch({ lang: code })}
                    aria-pressed={s.lang === code}
                    className={cn(
                        'px-3 py-1 rounded-full border-[1.5px] text-xs font-semibold whitespace-nowrap transition-colors',
                        s.lang === code ? 'border-primary bg-primary/10 text-primary' : 'border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                    )}
                >
                    {label}
                </button>
            ))}
        </div>
    );

    return (
        <PosterEditor
            icon={<IconFileDownload size={20} />}
            title={t('rateChart.top.title')}
            titleShort={t('rateChart.top.titleShort')}
            onClose={onClose}
            extras={langPills}
            metaLine={metaLine}
            tabs={tabs}
            pageSize={{ w: SHEET_W, h: H }}
            pageCount={pageCount}
            fitKey={`${s.layout}|${s.cols}|${s.paper}|${s.density}`}
            renderPages={({ preview }) => pageList.map((pg, i) => (
                <SheetPage key={i} m={m} columns={columnsOf(pg)} index={i} total={pageList.length} preview={preview} />
            ))}
            exportConfig={{
                stem: fileStem(s.shop, 'rate-chart'),
                sheetMm: (PAPERS[s.paper] || PAPERS.A4).mm,
                imageFormat: 'png',
                prepare: loadPosterFonts,
                ready: rows.length > 0,
                emptyMessage: t('rateChart.export.noServices'),
                meta: [s.paper, countLabel('top.page', pageCount)].join(' · '),
            }}
        >
            {/* Off-screen: the blocks that are measured for page breaks */}
            <SheetRuler m={m} rows={rows} rulerRef={rulerRef} />
        </PosterEditor>
    );
}
